/**
 * What a person sent with a message (slice 2f spec §10): its photos, shown
 * from Crew's copy (`GET /api/files/:id`), and its other files, as downloads.
 * A photo opens full size in a dialog. Photos a held message passed on to a
 * later text (`photos_from`) show on the held message only.
 */
import { Dialog, DialogContent, DialogTitle } from "@crew/ui/components/dialog";
import { cn } from "@crew/ui/lib/utils";
import { Button } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { FilePlus, FileText, ImageOff, Forward, Paperclip } from "lucide-react";
import { errorHint } from "../../app/api";
import { useCreateFileDoc } from "../docs/api";
import { sizeLabel } from "../files/kinds";
import { useState } from "react";
import { attachmentsOf, fileUrl, forwardedSubject, type Attachment } from "./attached";

/** A photo's name without its extension, for its alt text. */
const stem = (name: string) => name.replace(/\.[^.]+$/, "") || "photo";

function PhotoTile({
  photo,
  from,
  single,
  onOpen,
}: {
  photo: Attachment;
  from: string;
  single: boolean;
  onOpen: () => void;
}) {
  const [state, setState] = useState<"loading" | "shown" | "gone">("loading");
  const alt = `Photo from ${from} (${stem(photo.name)})`;
  const frame = cn(
    "relative overflow-clip rounded-xl bg-muted shadow-ring",
    single ? "w-[min(240px,70vw)]" : "aspect-square w-full",
    single && state !== "shown" && "aspect-[4/3]",
  );
  if (state === "gone") {
    return (
      <div
        data-photo={photo.id}
        data-state="gone"
        className={cn(frame, "flex items-center justify-center")}
      >
        <span className="flex flex-col items-center gap-1 px-3 text-center text-xs text-muted-foreground">
          <ImageOff aria-hidden className="size-4" />
          Photo no longer available
        </span>
      </div>
    );
  }
  return (
    <button
      type="button"
      data-photo={photo.id}
      data-state={state}
      onClick={onOpen}
      aria-label={`Open ${alt}`}
      className={cn(
        frame,
        "block cursor-zoom-in focus-visible:outline-2 focus-visible:outline-ring",
      )}
    >
      <img
        src={fileUrl(photo.id, "thumb")}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setState("shown")}
        onError={() => setState("gone")}
        className={cn(
          "block",
          single ? "h-auto max-h-80 w-full object-cover" : "size-full object-cover",
          state === "loading" && "absolute inset-0 size-full opacity-0",
        )}
      />
    </button>
  );
}

/**
 * Save to Docs, or Open doc once it's one (slice 5b spec §6.3): the file
 * stays in its message too, and the doc is linked to the chat.
 */
function KeepInDocs({ file, threadId }: { file: Attachment; threadId: string }) {
  const save = useCreateFileDoc();
  const docId = file.doc_id ?? save.data?.id ?? null;
  if (docId) {
    return (
      <Link
        href={`/docs/${docId}`}
        data-file-doc-link
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-foreground underline-offset-2 hover:underline"
      >
        <FileText aria-hidden className="size-3 text-copper" />
        Open doc
      </Link>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <Button
        size="xs"
        variant="ghost"
        data-file-save
        disabled={save.isPending}
        onClick={() => save.mutate({ file_id: file.id, thread_id: threadId })}
      >
        <FilePlus />
        Save to Docs
      </Button>
      {save.error ? (
        <span role="alert" className="text-xs text-brick">
          {errorHint(save.error)}
        </span>
      ) : null}
    </span>
  );
}

function FileChip({ file, threadId }: { file: Attachment; threadId?: string }) {
  // An email's forwarded original reads as what it is (slice 2h §8.3): "Forwarded · Invoice #178054".
  const forwarded = forwardedSubject(file.name);
  const chip = (
    <a
      href={fileUrl(file.id)}
      download={file.name}
      data-file={file.id}
      {...(forwarded !== null ? { "data-forwarded": "" } : {})}
      className="inline-flex max-w-full items-center gap-1.5 self-start rounded-md bg-card px-2 py-1 text-xs font-medium text-foreground shadow-ring hover:bg-card/80"
    >
      {forwarded !== null ? (
        <Forward aria-hidden className="size-3 shrink-0 text-copper" />
      ) : (
        <Paperclip aria-hidden className="size-3 shrink-0" />
      )}
      <span className="truncate">
        {forwarded !== null ? `Forwarded · ${forwarded}` : file.name}
      </span>
      <span className="shrink-0 font-normal text-muted-foreground">{sizeLabel(file.size)}</span>
    </a>
  );
  if (!threadId) return chip;
  return (
    <span data-file-row className="flex flex-wrap items-center gap-1 self-start">
      {chip}
      <KeepInDocs file={file} threadId={threadId} />
    </span>
  );
}

/** A message's photos (one alone, two to four two-up) and its files. */
export function Attachments({
  data,
  from,
  threadId,
}: {
  data: Record<string, unknown>;
  /** Who sent them, for the photos' alt text. */
  from: string;
  /** The chat they're in: Save to Docs links it (slice 5b). Absent while the message is unsent. */
  threadId?: string;
}) {
  const { photos, files } = attachmentsOf(data);
  const [open, setOpen] = useState<Attachment | null>(null);
  if (photos.length === 0 && files.length === 0) return null;
  const single = photos.length === 1;
  return (
    <div data-attachments className="mt-1.5 flex flex-col gap-1.5">
      {photos.length > 0 ? (
        <div className={cn(single ? "flex" : "grid w-[min(248px,72vw)] grid-cols-2 gap-1")}>
          {photos.map((p) => (
            <PhotoTile key={p.id} photo={p} from={from} single={single} onOpen={() => setOpen(p)} />
          ))}
        </div>
      ) : null}
      {files.map((f) => (
        <FileChip key={f.id} file={f} threadId={threadId} />
      ))}
      <Dialog open={open !== null} onOpenChange={(next) => (next ? null : setOpen(null))}>
        <DialogContent className="w-auto max-w-[calc(100vw-2rem)] gap-2 p-2 sm:max-w-[min(90vw,1100px)]">
          <DialogTitle className="sr-only">{open ? `Photo from ${from}` : "Photo"}</DialogTitle>
          {open ? (
            <img
              src={fileUrl(open.id, "full")}
              alt={`Photo from ${from} (${stem(open.name)})`}
              className="h-auto max-h-[85vh] w-auto max-w-[calc(100vw-3rem)] rounded-lg object-contain sm:max-w-[min(88vw,1084px)]"
            />
          ) : null}
          {open && threadId ? (
            <div className="flex justify-end px-1 pb-1">
              <KeepInDocs file={open} threadId={threadId} />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
